import Phaser from 'phaser';
import {
  INVENTORY_COLS,
  INVENTORY_ROWS,
  SLOT_SIZE,
  SLOT_GAP,
  EQUIP_SLOTS,
} from '../constants';
import { InventoryManager } from '../systems/InventoryManager';

/**
 * InventoryScene
 * ──────────────
 * Toggleable overlay showing the player's backpack grid and equipment
 * slots.  Launched / stopped by GameScene when the player presses 'I'.
 */
export class InventoryScene extends Phaser.Scene {
  constructor() {
    super({ key: 'InventoryScene' });
  }

  create() {
    const { width, height } = this.scale;

    // Shared inventory data – create once, persist across open/close
    if (!this.registry.has('inventoryManager')) {
      this.registry.set('inventoryManager', new InventoryManager());
    }
    this.inventory = this.registry.get('inventoryManager');

    // ── Dark overlay ────────────────────────────────────────────
    this.overlay = this.add.graphics();
    this.overlay.fillStyle(0x000000, 0.6);
    this.overlay.fillRect(0, 0, width, height);

    // ── Panel background ────────────────────────────────────────
    const panelW = 460;
    const panelH = 380;
    const panelX = (width - panelW) / 2;
    const panelY = (height - panelH) / 2;

    this.panel = this.add.graphics();
    this.panel.fillStyle(0x1a1a2e, 0.95);
    this.panel.fillRoundedRect(panelX, panelY, panelW, panelH, 12);
    this.panel.lineStyle(2, 0x555577, 0.8);
    this.panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 12);

    // Title
    this.add
      .text(width / 2, panelY + 20, 'INVENTORY', {
        fontFamily: 'monospace',
        fontSize: '20px',
        color: '#ccccdd',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 0);

    // ── Equipment slots (left side) ─────────────────────────────
    const equipX = panelX + 24;
    const equipStartY = panelY + 60;

    EQUIP_SLOTS.forEach((slot, i) => {
      const sy = equipStartY + i * (SLOT_SIZE + SLOT_GAP);
      this.add.image(equipX + SLOT_SIZE / 2, sy + SLOT_SIZE / 2, 'slot');

      // Slot label
      this.add
        .text(equipX + SLOT_SIZE + 8, sy + SLOT_SIZE / 2, slot.toUpperCase(), {
          fontFamily: 'monospace',
          fontSize: '11px',
          color: '#888899',
        })
        .setOrigin(0, 0.5);

      // Show equipped item name if any
      const equipped = this.inventory.equipment[slot];
      if (equipped) {
        this.add
          .text(equipX + SLOT_SIZE / 2, sy + SLOT_SIZE / 2, equipped.name[0], {
            fontFamily: 'monospace',
            fontSize: '18px',
            color: '#ffffff',
          })
          .setOrigin(0.5);
      }
    });

    // ── Backpack grid (right side) ──────────────────────────────
    const gridStartX = panelX + 180;
    const gridStartY = panelY + 60;

    for (let row = 0; row < INVENTORY_ROWS; row++) {
      for (let col = 0; col < INVENTORY_COLS; col++) {
        const sx = gridStartX + col * (SLOT_SIZE + SLOT_GAP);
        const sy = gridStartY + row * (SLOT_SIZE + SLOT_GAP);
        this.add.image(sx + SLOT_SIZE / 2, sy + SLOT_SIZE / 2, 'slot');

        const idx = row * INVENTORY_COLS + col;
        const item = this.inventory.backpack[idx];
        if (item) {
          this.add
            .text(sx + SLOT_SIZE / 2, sy + SLOT_SIZE / 2, item.name.substring(0, 3), {
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#ffffff',
            })
            .setOrigin(0.5);
        }
      }
    }

    // ── Close hint ──────────────────────────────────────────────
    this.add
      .text(width / 2, panelY + panelH - 20, 'Press I to close', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#666677',
      })
      .setOrigin(0.5, 1);
  }
}
